import React, { useState } from 'react';
import Spinner from '@dashboard/components/Spinner';
import Callout from '@dashboard/components/Callout';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import Button from '@dashboard/components/Button';
import ApiKeyDeleteDialog from './ApiKeyDeleteDialog';
import StatusBadge from '@/dashboard/components/StatusBadge';
import { formatDateTime } from '@/dashboard/lib/format-date';
import Dialog from '@dashboard/components/Dialog';
import Tooltip from '@dashboard/components/Tooltip';
import EventsLog from '@dashboard/components/EventsLog/EventsLog';

const ApiKeyTable = ({ data, isLoading, isError }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState(null);

  const formatLastUsed = lastUsedAt => {
    if (!lastUsedAt) return 'Never';
    return formatDateTime(lastUsedAt);
  };

  const getStatusAppearance = isDeleted => {
    return isDeleted ? 'error' : 'success';
  };

  const handleDeleteClick = apiKey => {
    setSelectedApiKey(apiKey);
    setDeleteDialogOpen(true);
  };

  return (
    <>
      {isLoading && <Spinner />}

      {isError && <Callout variant="error">Failed to load API keys</Callout>}

      {data && data.apiKeys?.length === 0 && !isLoading && !isError && (
        <Callout variant="info" padded={false}>
          No API keys found. Create your first API key to get started.
        </Callout>
      )}

      {data && data.apiKeys?.length > 0 && (
        <div className="overflow-auto">
          <CardListItem variant="header" className="!mt-0">
            <CardListItemElement size="fill" className="!pl-0">
              Name
            </CardListItemElement>
            <CardListItemElement size="large">Key preview</CardListItemElement>
            <CardListItemElement size="xl">Last used</CardListItemElement>
            <CardListItemElement size="medium">Usage</CardListItemElement>
            <CardListItemElement size="medium">Status</CardListItemElement>
            <CardListItemElement size="2xl">Created</CardListItemElement>
            <CardListItemElement size="xl">Actions</CardListItemElement>
          </CardListItem>

          {data.apiKeys.map(apiKey => (
            <CardListItem key={apiKey.id}>
              <CardListItemElement size="fill" className="!pl-0">
                {apiKey.name}
              </CardListItemElement>

              <CardListItemElement size="large">
                <div className="flex items-center gap-2">
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">{apiKey.keyPreview}</code>
                </div>
              </CardListItemElement>

              <CardListItemElement size="xl">
                {formatLastUsed(apiKey.lastUsedAt)}
              </CardListItemElement>

              <CardListItemElement size="medium" align="center">
                {apiKey.usageCount || 0}
              </CardListItemElement>

              <CardListItemElement size="medium">
                <StatusBadge
                  appearance={getStatusAppearance(apiKey.isDeleted)}
                  label={apiKey.isDeleted ? 'Inactive' : 'Active'}
                ></StatusBadge>
              </CardListItemElement>

              <CardListItemElement size="2xl">
                {formatDateTime(apiKey.createdAt)}
              </CardListItemElement>

              <CardListItemElement size="xl" className="!pr-0">
                <div className="flex items-center gap-2">
                  {apiKey.integrationId && (
                    <Tooltip label="View logs" position="bottom">
                      <Dialog
                        title="Integration logs"
                        cancelButton={null}
                        confirmButton={null}
                        trigger={
                          <Button appearance="secondary" size="small">
                            Logs
                          </Button>
                        }
                        size="5xl"
                      >
                        <EventsLog integrationId={apiKey.integrationId} hideResend={true} />
                      </Dialog>
                    </Tooltip>
                  )}

                  <Button
                    appearance="secondary"
                    size="small"
                    onClick={() => handleDeleteClick(apiKey)}
                  >
                    Delete
                  </Button>
                </div>
              </CardListItemElement>
            </CardListItem>
          ))}
        </div>
      )}

      <ApiKeyDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedApiKey(null);
        }}
        apiKey={selectedApiKey}
      />
    </>
  );
};

export default ApiKeyTable;
